import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, Button as MUIButton, Dialog, DialogContent } from "@mui/material";
import FormContainer from "../components/FormContainer";
import FloatingLabelInput from "../components/FloatingLabelInput";
import Button from "../components/Button";
import TextArea from "../components/TextArea";
import CitySearchInput from "../components/CitySearchInput";
import DateInput from "../components/DateInput";
import Dropdown from "../components/Dropdown";
import ReasonSearchDropdown from "../components/ReasonSearchDropdown";
import ContactSearchDropdown from "../components/ContactSearchDropdown";
import CloseIcon from '@mui/icons-material/Close';
import { ReactSortable } from 'react-sortablejs';
import { generateTimeOptions, getTodayDate, getFutureDate, formatDateForInput } from "./utils/ComplainDateLogic";
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { useAuth } from '../context/AuthContext';

// Static Status Options
const staticStatusOptions = [{ value: "Open", label: "Open" }, { value: "Close", label: "Close" }];

function ComplainForm({ onClose, ComplainData: propComplainData, mode, connectionString }) {

    const { user, selectedCompany, loading } = useAuth();
    const path = connectionString;
    const uId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory || null;
    const navigate = useNavigate();
    const location = useLocation();
    const [formErrors, setFormErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const solutionFileInputRef = useRef(null); // New ref for solution files
    const [fetchedFiles, setFetchedFiles] = useState([]);
    const [files, setFiles] = useState([]);
    const [solutionFiles, setSolutionFiles] = useState([]); // New state for solution files
    const [previewFile, setPreviewFile] = useState(null);
    const [serverFileIds, setServerFileIds] = useState([]);

    // Safely assign record
    const record = propComplainData?.complain || location.state?.ComplainData?.complain || location.state?.record;
    const overallMode = onClose ? "followup" : mode || location.state?.mode || "insert";
    const [displayMode] = useState(overallMode);
    const [clientMode, setClientMode] = useState(record ? "existClient" : "newClient");

    // State variables
    const [complainId, setComplainId] = useState("");
    const [staticStatus, setStaticStatus] = useState("");
    const [complainNumber, setComplainNumber] = useState("");
    const [ComplainDate, setComplainDate] = useState(overallMode === "insert" && !record ? getTodayDate() : "");
    const [userId, setUserId] = useState("");
    const [employeeName, setEmployeeName] = useState("");
    const [leadStatusID, setLeadStatusID] = useState("");
    const [leadStatusValue, setLeadStatusValue] = useState("");
    const [priorityID, setPriorityID] = useState("");
    const [priorityValue, setPriorityValue] = useState("");
    const [mobile, setMobile] = useState("");
    const [clientName, setClientName] = useState("");
    const [gstInNumber, setGstInNumber] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [email, setEmail] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [stateField, setStateField] = useState("");
    const [stateCode, setStateCode] = useState("");
    const [Complaint, setComplaint] = useState("");
    const [remarks, setRemarks] = useState("");
    const [solution, setSolution] = useState("");
    const [complainFollowUpDate, setcomplainFollowUpDate] = useState(overallMode === "insert" && !record ? getFutureDate() : "");
    const [pincode, setPincode] = useState("");
    const [clientId, setClientId] = useState("");
    const [complainBy, setComplainBy] = useState("");
    const [complainMobile, setComplainMobile] = useState("");
    const [qty, setQty] = useState("");
    const [rate, setRate] = useState("");
    const [amount, setAmount] = useState(0);
    const [statusOptions, setStatusOptions] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    const [priorityOptions, setPriorityOptions] = useState([]);
    const [cities, setCities] = useState([]);
    const [reasonOptions, setReasonOptions] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [showAddNewReasonModal, setShowAddNewReasonModal] = useState(false);
    const [isDisabled, setIsDisabled] = useState(record ? true : false);
    const [isFailed, setIsFailed] = useState(false);
    const [followUpTime, setFollowUpTime] = useState("10:00");
    // Add new state for fetched solution files
    const [fetchedSolutionFiles, setFetchedSolutionFiles] = useState([]);

    // Helper to create unique id
    const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    // File to base64 converter
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            if (!(file instanceof Blob)) {
                console.error('[fileToBase64] Invalid file object:', file);
                return reject(new Error('Invalid file: Not a Blob or File object'));
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    const handleFileChange = async (e, isSolutionFile = false) => {
        console.log('[handleFileChange] Event triggered:', e.target.files?.length || 0, 'files detected', { isSolutionFile });
        const selectedFiles = Array.from(e.target.files || []);
        console.log('[handleFileChange] Selected files:', selectedFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));

        if (selectedFiles.length === 0) {
            console.log('[handleFileChange] No files selected');
            alert('No files selected. Please choose image or video files.');
            return;
        }

        const validFiles = selectedFiles
            .filter((file) => {
                if (!(file instanceof File)) {
                    console.warn('[handleFileChange] Skipping invalid file object:', file);
                    return false;
                }
                if (file.size > 20 * 1024 * 1024) {
                    alert(`${file.name} exceeds 20 MB limit and will be skipped.`);
                    return false;
                }
                if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
                    alert(`${file.name} is not an image or video and will be skipped.`);
                    return false;
                }
                return true;
            })
            .map((file, idx) => ({ id: makeId(), file, name: file.name, isNew: true, sequence: (isSolutionFile ? solutionFiles.length : fetchedFiles.length) + idx + 1, mimeType: file.type }));

        console.log('[handleFileChange] Valid files to add:', validFiles);
        if (isSolutionFile) {
            setSolutionFiles(prev => {
                const updatedFiles = [...prev, ...validFiles];
                console.log('[handleFileChange] Updated solutionFiles state:', updatedFiles);
                return updatedFiles;
            });
            if (solutionFileInputRef.current) {
                console.log('[handleFileChange] Solution file input before reset:', solutionFileInputRef.current.files?.length || 0);
                solutionFileInputRef.current.value = "";
                console.log('[handleFileChange] Solution file input reset');
            }
        } else {
            setFiles(prev => {
                const updatedFiles = [...prev, ...validFiles];
                console.log('[handleFileChange] Updated files state:', updatedFiles);
                return updatedFiles;
            });
            if (fileInputRef.current) {
                console.log('[handleFileChange] File input before reset:', fileInputRef.current.files?.length || 0);
                fileInputRef.current.value = "";
                console.log('[handleFileChange] File input reset');
            }
        }
    };

    const handleRemoveFile = (id, isNew, isSolutionFile = false) => {
        if (isSolutionFile) {
            setSolutionFiles(prev => {
                const updated = prev.filter(f => f.id !== id);
                console.log('[handleRemoveFile] Removed solution file, updated solutionFiles:', updated);
                return updated;
            });
        } else if (isNew) {
            setFiles(prev => {
                const updated = prev.filter(f => f.id !== id);
                console.log('[handleRemoveFile] Removed new file, updated files:', updated);
                return updated;
            });
        } else {
            setFetchedFiles(prev => {
                const updated = prev.filter(f => String(f.id) !== String(id));
                console.log('[handleRemoveFile] Removed fetched file, updated fetchedFiles:', updated);
                return updated;
            });
        }
    };

    const handleFileButtonClick = (isSolutionFile = false) => {
        console.log('[handleFileButtonClick] Triggering file input click', { isSolutionFile });
        if (isSolutionFile) {
            solutionFileInputRef.current?.click();
        } else {
            fileInputRef.current?.click();
        }
    };

    const handleOpenPreview = (file) => { 
        console.log('[handleOpenPreview] Opening preview for file:', file);
        setPreviewFile(file); 
    };

    const handleClosePreview = () => { 
        console.log('[handleClosePreview] Closing preview');
        setPreviewFile(null); 
    };

    // NEW: Update allFiles to include only complaint files (C)
    const allFiles = [
        ...fetchedFiles.map((f, i) => ({ id: f.id, url: f.url, name: f.name, sequence: f.sequence || (i + 1), isNew: false, mimeType: f.mimeType || 'unknown', complainFileDtsId: f.complainFileDtsId || null, isSolutionFile: false })),
        ...files.map((f, i) => ({ id: f.id, url: URL.createObjectURL(f.file), name: f.name, sequence: fetchedFiles.length + i + 1, isNew: true, mimeType: f.mimeType, isSolutionFile: false }))
    ];

    // NEW: Add allFetchedSolutionFiles for displaying existing solution files in followup
    const allFetchedSolutionFiles = fetchedSolutionFiles.map((f, i) => ({ id: f.id, url: f.url, name: f.name, sequence: f.sequence || (i + 1), isNew: false, mimeType: f.mimeType, isSolutionFile: true }));

    const allSolutionFiles = solutionFiles.map((f, i) => ({ id: f.id, url: URL.createObjectURL(f.file), name: f.name, sequence: i + 1, isNew: true, mimeType: f.mimeType, isSolutionFile: true }));

    // Update the useEffect for fetching files
    useEffect(() => {
        if (overallMode !== "insert" && complainId && companyId) {
            const fetchFiles = async () => {
                try {
                    // NEW: Determine types param - fetch "C" only for edit, "C,S" for followup
                    const typesParam = overallMode === "followup" ? "C,S" : "C";
                    console.log('[fetchFiles] Fetching files for complainId:', complainId, 'companyId:', companyId, 'types:', typesParam);
                    const response = await axios.get(`${path}/api/complain-files`, {
                        params: { complainId, companyId, types: typesParam }
                    });

                    // NEW: Handle separate arrays from backend response
                    const complaintFiles = response.data.complaintFiles || [];
                    const solutionFiles = response.data.solutionFiles || [];

                    // Process complaint files (for main display)
                    const complaintPreviews = complaintFiles.map(file => ({
                        id: file.id,
                        url: file.image ? `data:${file.mimeType};base64,${file.image}` : null,
                        name: file.fileName || 'Unknown',
                        sequence: file.sequence || 0,
                        isNew: false,
                        mimeType: file.mimeType || 'unknown',
                        complainFileDtsId: file.id,
                        isSolutionFile: false,
                        type: file.type // NEW: Store type for reference
                    }));

                    // NEW: Process solution files (only for followup mode)
                    const solutionPreviews = solutionFiles.map(file => ({
                        id: file.id,
                        url: file.image ? `data:${file.mimeType};base64,${file.image}` : null,
                        name: file.fileName || 'Unknown',
                        sequence: file.sequence || 0,
                        isNew: false,
                        mimeType: file.mimeType || 'unknown',
                        complainFileDtsId: file.id,
                        isSolutionFile: true, // NEW: Mark as solution file
                        type: file.type
                    }));

                    console.log('[fetchFiles] Fetched complaint files:', complaintPreviews);
                    console.log('[fetchFiles] Fetched solution files:', solutionPreviews);

                    setFetchedFiles(complaintPreviews);
                    setServerFileIds(complaintPreviews.map(f => f.id));

                    // NEW: Set fetched solution files only in followup mode
                    if (overallMode === "followup") {
                        setFetchedSolutionFiles(solutionPreviews);
                    } else {
                        setFetchedSolutionFiles([]);
                    }
                } catch (error) {
                    console.error('[fetchFiles] Error fetching files:', { message: error.message, response: error.response?.data });
                    setFetchedFiles([]);
                    setServerFileIds([]);
                    setFetchedSolutionFiles([]);
                }
            };
            fetchFiles();
        }
    }, [complainId, companyId, overallMode]);

    // Form submission handler
    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors([]);
            const errors = [];
            if (!clientName.trim()) errors.push("Client Name is mandatory");
            if (!mobile.trim()) errors.push("Mobile is mandatory");
            else if (mobile.trim().length !== 10) errors.push("Mobile No must be exactly 10 digits");
            if (!contactPerson.trim()) errors.push("Contact Person is mandatory");
            if (!complainMobile.trim()) errors.push("Complain Mobile is mandatory");
            else if (complainMobile.trim().length !== 10) errors.push("Complain Mobile must be exactly 10 digits");
            if (gstInNumber && gstInNumber.trim() !== "" && gstInNumber.trim().length !== 15) errors.push("GST No must be exactly 15 characters");
            if (overallMode === "insert" && new Date(complainFollowUpDate) < new Date(ComplainDate)) {
                errors.push("Follow-up date cannot be earlier than Inquiry date.");
            }
            if (errors.length > 0) {
                setFormErrors(errors);
                setIsSubmitting(false);
                console.log('[handleSubmit] Form validation failed:', errors);
                return;
            }
            setIsSubmitting(true);

            // Prepare submission data
            const submissionData = { ComplainDate, complainNumber, userId, employeeName, leadStatusID, leadStatusValue, priorityID, priorityValue, mobile, clientName, gstInNumber, contactPerson, email, address, city, state: stateField, stateCode, Complaint, remarks, solution, complainFollowUpDate, pincode, clientId, complainBy, complainMobile, staticStatus, statusName: staticStatus, followUpTime: followUpTime + ':00', companyId, uId };
            try {
                if (overallMode === "edit") {
                    if (!complainId) {
                        console.error('[handleSubmit] Complain ID missing for update');
                        alert("Complain ID missing for update!");
                        setIsSubmitting(false);
                        return;
                    }

                    // Update complain data
                    console.log('[handleSubmit] Updating complaint with data:', submissionData);
                    const response = await axios.put(`${path}/api/complainupdate/${complainId}`, { submissionData });
                    if (response.status === 200 && response.data.success) {
                        // Handle image updates
                        const currentIds = allFiles.filter(f => f.complainFileDtsId && !f.isNew).map(f => f.complainFileDtsId);
                        const newImages = files.filter(f => f.isNew && f.file instanceof Blob);
                        const reordered = allFiles.filter(f => f.complainFileDtsId && !f.isNew);

                        console.log('[handleSubmit] Files state:', files);
                        console.log('[handleSubmit] New images:', newImages);
                        console.log('[handleSubmit] Current file IDs:', currentIds);
                        console.log('[handleSubmit] Reordered files:', reordered);

                        if (newImages.length > 0 || currentIds.length !== serverFileIds.length || reordered.length > 0) {
                            const imageUpdatePayload = { complainId, companyId, complainFileDtsIds: currentIds, newFiles: [],
                                updateSequences: reordered.map((f, idx) => ({
                                    id: f.complainFileDtsId,
                                    sequence: idx + 1
                                }))
                            };

                            for (const img of newImages) {
                                console.log('[handleSubmit] Processing new file:', img);
                                try {
                                    const base64 = await fileToBase64(img.file);
                                    imageUpdatePayload.newFiles.push({
                                        base64,
                                        name: img.name,
                                        mimeType: img.mimeType,
                                        sequence: img.sequence
                                    });
                                    console.log('[handleSubmit] Successfully converted file to base64:', img.name);
                                } catch (base64Err) {
                                    console.error('[handleSubmit] Failed to convert file to base64:', img, base64Err);
                                    throw new Error(`Failed to convert file ${img.name} to base64: ${base64Err.message}`);
                                }
                            }

                            console.log('[handleSubmit] Image update payload:', imageUpdatePayload);

                            if (imageUpdatePayload.newFiles.length > 0 || imageUpdatePayload.complainFileDtsIds.length > 0 || imageUpdatePayload.updateSequences.length > 0) {
                                try {
                                    await axios.post(`${path}/api/complain-update-images`, imageUpdatePayload);
                                    console.log('[handleSubmit] Image update successful');
                                } catch (imgErr) {
                                    console.error('[handleSubmit] Image update failed:', imgErr);
                                    alert('Complaint updated, but image update failed.');
                                }
                            } else {
                                console.log('[handleSubmit] No image updates needed (no new files or changes)');
                            }
                        } else {
                            console.log('[handleSubmit] No file operations needed');
                        }
                        alert("Complaint updated successfully!");
                        navigate(-1);
                    } else {
                        console.error('[handleSubmit] Update failed:', response.data);
                        alert("Update failed!");
                    }
                } else if (overallMode === "followup") {
                    const followupData = { complainId, complainNumber, complainFollowUpDate, userId, employeeName, statusId: parseInt(leadStatusID) || 0, statusName: leadStatusValue, addUserId: parseInt(uId) || 0, EmployeeName: employeeName, Complaint, remark: remarks, solution, complainBy, complainMobile, lastpriorityid: parseInt(priorityID) || 0, lastpriority: priorityValue, staticStatus, statusNameFinal: staticStatus, followUpTime, companyId, uId };
                    const followupFormData = new FormData();
                    followupFormData.append('submissionData', JSON.stringify(followupData));
                    // append files (if any) to field name 'files' (server expects 'files' in multerWrapper)
                    files.forEach((fileObj, idx) => {
                        if (fileObj.file instanceof Blob) {
                            followupFormData.append('files', fileObj.file, fileObj.file.name);
                        } else {
                            console.warn('[handleSubmit] Skipping invalid file in followup:', fileObj);
                        }
                    });
                    console.log('[handleSubmit] Submitting followup with data:', followupData);
                    // IMPORTANT: don't override Content-Type — axios/browser will set with boundary
                    const followupResponse = await axios.post(`${path}/api/complainfollowup`, followupFormData);
                    if (followupResponse.status === 201) {
                        // Handle solution file uploads
                        if (solutionFiles.length > 0) {
                            const solutionUploadPayload = { complainId, companyId, uId, newFiles: [] };
                            for (const fileObj of solutionFiles) {
                                if (!(fileObj.file instanceof Blob)) {
                                    console.warn('[handleSubmit] Skipping invalid solution file:', fileObj);
                                    continue;
                                }
                                const base64 = await fileToBase64(fileObj.file);
                                solutionUploadPayload.newFiles.push({ base64, name: fileObj.name, mimeType: fileObj.mimeType, sequence: fileObj.sequence });
                            }
                            console.log('[handleSubmit] Solution file upload payload:', solutionUploadPayload);
                            try {
                                await axios.post(`${path}/api/solution-upload`, solutionUploadPayload);
                                console.log('[handleSubmit] Solution file upload successful');
                            } catch (imgErr) {
                                console.error('[handleSubmit] Solution file upload failed:', imgErr);
                                alert('Followup submitted, but solution file upload failed.');
                            }
                        }
                        alert("Followup submitted successfully!");
                        // if (typeof onClose === "function") {
                        //     onClose();
                        // }
                    } else {
                        console.error('[handleSubmit] Followup submission failed:', followupResponse.data);
                        alert("Followup submission failed!");
                    }
                } else {
                    if (!clientId) {
                        console.error('[handleSubmit] Client ID missing');
                        alert("Client ID is missing!");
                        setIsSubmitting(false);
                        return;
                    }
                    const formData = new FormData();
                    formData.append('submissionData', JSON.stringify(submissionData));
                    files.forEach((fileObj, idx) => {
                        if (fileObj.file instanceof Blob) {
                            formData.append('files', fileObj.file, fileObj.file.name);
                        } else {
                            console.warn('[handleSubmit] Skipping invalid file in insert:', fileObj);
                        }
                    });
                    console.log('[handleSubmit] Submitting new complaint with data:', submissionData);
                    const complainResponse = await axios.post(`${path}/api/complain-insert`, formData);
                    if (complainResponse.status === 201) {
                        const newComplainId = complainResponse.data.complainId;
                        if (files.length > 0) {
                            const imageUpdatePayload = { complainId: newComplainId, companyId, complainFileDtsIds: [], updateSequences: [], newFiles: [] };
                            for (const fileObj of files) {
                                if (!(fileObj.file instanceof Blob)) {
                                    console.warn('[handleSubmit] Skipping invalid file in insert image update:', fileObj);
                                    continue;
                                }
                                const base64 = await fileToBase64(fileObj.file);
                                imageUpdatePayload.newFiles.push({ base64, name: fileObj.name, mimeType: fileObj.mimeType, sequence: fileObj.sequence });
                            }
                            console.log('[handleSubmit] Insert image update payload:', imageUpdatePayload);
                            try {
                                await axios.post(`${path}/api/complain-update-images`, imageUpdatePayload);
                                console.log('[handleSubmit] Insert image update successful');
                            } catch (imgErr) {
                                console.error('[handleSubmit] Image upload failed:', imgErr);
                                alert('Complaint submitted, but image upload failed.');
                            }
                        }
                        alert("Complaint submitted successfully!");
                        clearFormFields();
                        navigate(-1);
                    } else {
                        console.error('[handleSubmit] Submission failed:', complainResponse.data);
                        alert("Submission failed!");
                    }
                }
            } catch (error) {
                console.error('[handleSubmit] Error during submission:', error);
                alert(`Something went wrong: ${error.message || error.response?.data?.message || 'Unknown error'}`);
            } finally {
                setIsSubmitting(false);
            }
        },
        [complainId, ComplainDate, complainNumber, userId, employeeName, leadStatusID, leadStatusValue, priorityID, priorityValue, mobile, clientName, gstInNumber, contactPerson, email, address, city, stateField, stateCode, Complaint, remarks, solution, complainFollowUpDate, pincode, clientId, complainBy, complainMobile, overallMode, clientMode, navigate, companyId, uId, staticStatus, followUpTime, files, fetchedFiles, serverFileIds, solutionFiles]
    );

    // Other useEffect hooks and handlers (unchanged)
    useEffect(() => {
        if (!record) return;
        setComplainId(record.complainId || record.id || "");
        setClientId(record.clientId || record.clientId || "");
        setClientName(record.clientname || record.FirmName || "");
        setMobile(record.mobileNo || record.MobileNo || "");
        setContactPerson(record.clientContact || record.ContactPerson || "");
        setEmail(record.emailId || record.EmailId || "");
        setGstInNumber(record.gstno || "");
        setAddress(record.address || record.Address || "");
        setCity(record.city || record.City || "");
        setStateField(record.state || record.state || "");
        setStateCode(record.StateCode || record.StateCode || "");
        setComplainNumber(record.complainNumber || "");
        setComplainBy(record.complainBy || "");
        setComplainMobile(record.complainMobile || "");
        setComplainDate(record.complainDate ? formatDateForInput(record.complainDate) : getTodayDate());
        setLeadStatusID(record.statusId ? String(record.statusId) : "");
        setLeadStatusValue(record.statusName || "");
        setPriorityID(record.priorityid ? String(record.priorityid) : "");
        setPriorityValue(record.priority || "");
        setComplaint(record.Issue || "");
        setRemarks(record.Remark || "");
        setSolution(record.Solution || "");
        setcomplainFollowUpDate(record.LastFollowUpDate ? formatDateForInput(record.LastFollowUpDate) : getFutureDate());
        setIsFailed(record.statusName && record.statusName.toLowerCase() === "Failed");
        setStaticStatus(record.statusName || "Open");
        const normalizedTime = record.followUpTime ? record.followUpTime.slice(0, 5) : "10:15";
        setFollowUpTime(normalizedTime);
        setUserId(record.UserId || uId || "");
        setEmployeeName(record.EmployeeName || user?.EmployeeName || "");
    }, [record, user]);

    useEffect(() => {
        if (user) {
            if (overallMode === "insert") {
                setUserId(uId);
                setEmployeeName(user.EmployeeName);
                setPriorityID("3");
                setPriorityValue("High");
                setLeadStatusID("1");
                setLeadStatusValue("Pipeline");
            } else if (record) {
                const data = record;
                if (data.UserId && data.EmployeeName) {
                    setUserId(data.UserId);
                    setEmployeeName(data.EmployeeName);
                } else {
                    setUserId(uId);
                    setEmployeeName(user.EmployeeName);
                }
            } else {
                setUserId(uId);
                setEmployeeName(user.EmployeeName);
            }
        }
    }, [user, overallMode, record]);

    useEffect(() => {
        if (userId && usersOptions.length > 0) {
            const found = usersOptions.some((user) => String(user.UserId) === String(userId));
            if (!found) {
                setUsersOptions((prev) => [...prev, { UserId: userId, employeeName }]);
            }
        }
    }, [userId, employeeName, usersOptions]);

    useEffect(() => {
        console.log(overallMode);
        if (overallMode === "insert") {
            const fetchcomplainNumber = async () => {
                try {
                    const response = await axios.get(`${path}/api/getComplainNumber`, {
                        params: {
                            companyId: companyId   // 👈 ADD HERE
                        }
                    });
                    console.log(response.data);
                    setComplainNumber(response.data.complainNumber);
                } catch (error) {
                    console.error("Error fetching ComplainNumber:", error);
                }
            };
            fetchcomplainNumber();
        }
    }, [overallMode]);

    useEffect(() => {
        axios.get(`${path}/api/status`).then((res) => { setStatusOptions(res.data.data || []); }).catch((error) => console.error("Error fetching status:", error));
        axios.get(`${path}/api/users`, { params: { companyId } }).then((res) => { setUsersOptions(Array.isArray(res.data.data) ? res.data.data : []); }).catch((error) => console.error("Error fetching users:", error));
        axios.get(`${path}/api/priority`).then((res) => {
            if (res.data.success) {
                setPriorityOptions(res.data.data || [{ PriorityId: "3", value: "High" }]);
            }
        }).catch((error) => { console.error("Error fetching priorities:", error); setPriorityOptions([{ PriorityId: "3", value: "High" }]); });
        axios.get(`${path}/api/get-cities`).then((res) => { setCities(res.data.cities || []); }).catch((error) => console.error("Error fetching cities:", error));
    }, [companyId]);

    useEffect(() => {
        if (overallMode === "followup") {
            axios.get(`${path}/api/reasons`, { params: { search: "" } }).then((res) => { setReasonOptions(res.data.data || []); }).catch((error) => console.error("Error fetching reason options:", error));
        }
    }, [overallMode]);

    useEffect(() => {
        const q = parseFloat(qty) || 0;
        const r = parseFloat(rate) || 0;
        setAmount(String((q * r).toFixed(2)));
    }, [qty, rate]);

    const handleAssignChange = (selected) => {
        setUserId(selected.UserId);
        setEmployeeName(selected.employeeName);
    };

    const handlePriorityChange = (selected) => {
        setPriorityID(selected.PriorityId);
        setPriorityValue(selected.value);
    };

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") e.preventDefault();
    }, []);

    const clearFormFields = () => {
        setComplainId("");
        setComplainNumber("");
        setComplainDate(getTodayDate());
        setUserId("");
        setEmployeeName("");
        setLeadStatusID("");
        setLeadStatusValue("");
        setPriorityID("");
        setPriorityValue("");
        setMobile("");
        setClientName("");
        setGstInNumber("");
        setContactPerson("");
        setEmail("");
        setAddress("");
        setCity("");
        setStateField("");
        setStateCode("");
        setComplaint("");
        setRemarks("");
        setSolution("");
        setcomplainFollowUpDate(getFutureDate());
        setPincode("");
        setClientId("");
        setComplainBy("");
        setComplainMobile("");
        setIsFailed(false);
        setStaticStatus("Open");
        setFollowUpTime("10:15");
        setFiles([]);
        setSolutionFiles([]);
        setFetchedFiles([]);
        setServerFileIds([]);
    };

    const handleClose = useCallback(() => {
        if (overallMode === "followup") {
            if (typeof onClose === "function") {
                onClose();
            }
        } else {
            navigate(-1);
        }
    }, [navigate, overallMode, onClose]);

    const disableLeftSection = overallMode === "followup" ? true : overallMode === "edit" ? true : isDisabled;
    console.log(overallMode, displayMode);
    const inquiryLabel = displayMode.toLowerCase() === "edit" ? "Edit Complaint" : displayMode.toLowerCase() === "followup" ? "Complaint Follow-Up" : "Add new Complaint";

    return (
        <FormContainer>
            <Box onKeyDown={handleKeyDown} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 1, borderRadius: 1 }}>
                <Typography variant="h5" align="center" sx={{ fontWeight: "bold", mb: 4, textDecoration: "underline", textUnderlineOffset: 8 }}>
                    Complaint Form -{" "}
                    <Typography component="span" variant="subtitle2" sx={{ mt: -1, color: "error.main", fontWeight: "semibold" }}> {inquiryLabel} </Typography>
                </Typography>

                {/* Top Row: Complaint No, Date, Status, Priority */}
                <Grid container spacing={2} sx={{ bgcolor: "grey.50", p: 2, border: "1px solid", borderColor: "grey.300", borderRadius: 1, fontSize: "0.875rem", fontWeight: 600, width: "95%", mx: "auto", mb: 2 }}>
                    <Grid item xs={12} sm={6} md={3}> <FloatingLabelInput label="Comp No." name="complainNumber" type="text" value={complainNumber} disabled fullWidth /> </Grid>
                    <Grid item xs={12} sm={6} md={3}> <DateInput label="Comp. Date" allowToday value={formatDateForInput(ComplainDate)} onChange={(e) => setComplainDate(e.target.value)} onTodaySelect={(todayValue) => setComplainDate(todayValue)} disabled={mode === "edit" || mode === "followup"} fullWidth /> </Grid>
                    <Grid item xs={12} sm={6} md={3}> <Dropdown id="staticStatus" name="staticStatus" label="Status" value={staticStatus} valueKey="value" labelKey="label" options={staticStatusOptions} onChange={(selected) => setStaticStatus(selected.value)} fullWidth /> </Grid>
                    <Grid item xs={12} sm={6} md={3}> <Dropdown id="priority" name="priority" label="Priority" valueKey="PriorityId" labelKey="value" value={priorityID} options={priorityOptions} onChange={handlePriorityChange} disabled={false} fullWidth /> </Grid>
                </Grid>

                {/* Main Form */}
                <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }} onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        {/* Left Column: Client and Address Details */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                <FloatingLabelInput label="Client Name" name="clientName" type="text" value={clientName} onChange={(e) => setClientName(e.target.value)} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Mobile" name="mobile" type="text" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, ""))} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Contact Person" name="contactPerson" type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} disabled={disableLeftSection} fullWidth required />
                                <FloatingLabelInput label="Email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={disableLeftSection} fullWidth />
                                <Box sx={{ width: "100%" }}> <TextArea label="Address" id="address" name="address" placeholder="Address" value={address} onChange={(e) => setAddress(e.target.value)} disabled={disableLeftSection} fullWidth /> </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", zIndex: 10, width: "100%" }}>
                                    <CitySearchInput value={city} flag={0} disabled={disableLeftSection} cities={cities} fullWidth connectionString={connectionString}
                                        onSelectCity={(cityObj) => {
                                            setCity(cityObj.city);
                                            setStateField(cityObj.state);
                                            setStateCode(cityObj.stateCode);
                                        }}
                                    />
                                    {stateField && (
                                        <Box sx={{ mt: 2, display: "flex", gap: 2, width: "100%" }}>
                                            <Box sx={{ width: "70%" }}> <FloatingLabelInput label="State" name="state" type="text" value={stateField} disabled readOnly fullWidth /> </Box>
                                            <Box sx={{ width: "30%" }}> <FloatingLabelInput label="State Code" name="stateCode" type="text" value={stateCode} disabled readOnly fullWidth /> </Box>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>

                        {/* Center Column: Followup Details */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: { md: 2 } }}>
                                {overallMode === "followup" && leadStatusValue === "Failed" && (
                                    <Box sx={{ width: "100%" }}>
                                        <ReasonSearchDropdown options={reasonOptions} onSelect={(option) => setSelectedReason(option)} onAddNew={() => setShowAddNewReasonModal(true)} fullWidth
                                            onSearch={async (searchTerm) => {
                                                try {
                                                    const res = await axios.get(`${path}/api/reasons`, { params: { search: searchTerm } });
                                                    setReasonOptions(res.data.data || []);
                                                } catch (error) {
                                                    console.error("Error searching reasons:", error);
                                                }
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box sx={{ width: "100%", py: 0 }}> <TextArea label="Complaint" id="Complaint" name="Complaint" placeholder="Complaint" value={Complaint} onChange={(e) => setComplaint(e.target.value)} disabled={overallMode === "followup" && leadStatusValue === "Failed"} fullWidth required /> </Box>
                                <Box sx={{ width: "100%" }}> <TextArea label="Remarks" id="remarks" name="remarks" placeholder="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={overallMode === "followup" && leadStatusValue === "Failed"} fullWidth /> </Box>
                                <Box sx={{ width: "100%" }}> <TextArea label="Solution" id="solution" name="solution" placeholder="Solution" value={solution} onChange={(e) => setSolution(e.target.value)} disabled={overallMode === "followup" && leadStatusValue === "Failed"} fullWidth /> </Box>
                                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 0.7, width: { sm: "100%" } }}>
                                    <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                                        <ContactSearchDropdown label="Complain By" value={complainBy} companyId={companyId} searchField="contactBy" fullWidth disabled={false} connectionString={connectionString}
                                            onSelect={(partial) => {
                                                if (partial.contactBy !== undefined) setComplainBy(partial.contactBy);
                                                if (partial.contactNumber !== undefined) setComplainMobile(partial.contactNumber);
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ width: { xs: "100%", sm: "50%" } }}>
                                        <ContactSearchDropdown label="Mobile No" value={complainMobile} companyId={companyId} searchField="contactNumber" fullWidth disabled={false} connectionString={connectionString}
                                            onSelect={(partial) => {
                                                if (partial.contactBy !== undefined) setComplainBy(partial.contactBy);
                                                if (partial.contactNumber !== undefined) setComplainMobile(partial.contactNumber);
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ width: "100%", pt: 1 }}> <Dropdown id="assign" name="assign" label="Assign" valueKey="UserId" labelKey="employeeName" value={userId} options={usersOptions} onChange={handleAssignChange} disabled={false} fullWidth /> </Box>
                                {staticStatus === "Open" && (
                                    <Box sx={{ display: "flex", width: "100%", pt: 0, alignItems: "center", gap: 2, mx: "auto" }}>
                                        <DateInput label="Follow-Up Date" allowToday value={overallMode === "insert" ? complainFollowUpDate : formatDateForInput(complainFollowUpDate)} onChange={(e) => setcomplainFollowUpDate(e.target.value)} onTodaySelect={(todayValue) => setcomplainFollowUpDate(todayValue)} disabled={false} fullWidth />
                                        <Dropdown id="followUpTime" name="followUpTime" label="Follow-Up Time" value={followUpTime} valueKey="value" labelKey="label" options={generateTimeOptions()} onChange={(selected) => setFollowUpTime(selected.value)} fullWidth />
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Right Column: Files */}
                        <Grid item xs={12} md={4}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pl: { md: 2 } }}>
                                <Box sx={{ width: "100%", mt: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}> {overallMode === "followup" ? "Complaint Files" : "Upload Files (Images/Videos, max 20MB each)"} </Typography>
                                    {overallMode !== "insert" && (
                                        <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}> {fetchedFiles.length > 0 ? `${fetchedFiles.length} existing file(s) loaded.` : "No existing files."} {overallMode !== "followup" && " Add new files below to upload."} </Typography>
                                    )}
                                    {files.length === 0 && overallMode !== "insert" && overallMode !== "followup" && (
                                        <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: "block" }}>
                                            No new files selected. Click "Select Files" to add new images/videos.
                                        </Typography>
                                    )}
                                    {overallMode !== "followup" && (
                                        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                                            <MUIButton variant="outlined" color="primary" onClick={() => handleFileButtonClick(false)} sx={{ textTransform: "none" }} disabled={isSubmitting}>
                                                Select Files
                                            </MUIButton>
                                            <Typography variant="body2" color="textSecondary"> {files.length > 0 ? `${files.length} new file(s) selected` : "No new files selected"} {allFiles.length > 0 && ` (${allFiles.length} total)`} </Typography>
                                            <input type="file" multiple accept="image/*,video/*" ref={fileInputRef} style={{ display: "none" }}
                                                onChange={(e) => {
                                                    console.log('[FileInput] onChange triggered');
                                                    handleFileChange(e, false);
                                                }}
                                            />
                                        </Box>
                                    )}
                                    {allFiles.length > 0 && (
                                        <Box sx={{ mt: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Files</Typography>
                                            <ReactSortable list={allFiles} animation={200} className="file-sortable-container" style={{ display: 'flex', flexWrap: 'wrap', marginTop: '16px' }}
                                                setList={(newState) => {
                                                    const updatedFiles = newState.filter(f => f.isNew).map(f => files.find(file => file.id === f.id)).filter(f => f && f.file instanceof Blob);
                                                    const updatedFetchedFiles = newState.filter(f => !f.isNew);
                                                    console.log('[ReactSortable] Updated files:', updatedFiles);
                                                    console.log('[ReactSortable] Updated fetchedFiles:', updatedFetchedFiles);
                                                    setFiles(updatedFiles);
                                                    setFetchedFiles(updatedFetchedFiles);
                                                }}
                                                disabled={overallMode === "followup"}
                                            >
                                                {allFiles.map((file, idx) => (
                                                    <Box key={file.id} onClick={() => handleOpenPreview(file)}
                                                        sx={{ position: "relative", width: 100, height: 100, borderRadius: 1, overflow: "hidden", border: `1px solid ${themeColors.neutral.lineStrong}`, mx: 1, display: "inline-block", "&:hover .remove-btn": { opacity: 1 }, cursor: overallMode === "followup" ? "pointer" : "move" }}
                                                    >
                                                        {file.url && file.mimeType.toLowerCase().startsWith("image/") ? (
                                                            <img src={file.url} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                    console.error(`[Image] Failed to load image: ${file.name}, MIME: ${file.mimeType}`);
                                                                }}
                                                            />
                                                        ) : file.url && file.mimeType.toLowerCase().startsWith("video/") ? (
                                                            <video style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                    console.error(`[Video] Failed to load video: ${file.name}, MIME: ${file.mimeType}`);
                                                                }}
                                                            >
                                                                <source src={file.url} type={file.mimeType} /> Your browser does not support the video tag.
                                                            </video>
                                                        ) : (
                                                            <Box sx={{ p: 1, bgcolor: "error.light", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                <Typography variant="caption" color="error">
                                                                    {file.url ? `Unsupported: ${file.mimeType}` : `No data for ${file.name}`}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {overallMode !== "followup" && (
                                                            <Box className="remove-btn" sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0, 0, 0, 0.3)", opacity: 0, transition: "opacity 0.2s ease-in-out" }} >
                                                                <CloseIcon fontSize="medium" sx={{ cursor: "pointer", color: "white", backgroundColor: "rgba(0,0,0,0.6)", borderRadius: "50%", padding: 0.5 }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRemoveFile(file.id, file.isNew, file.isSolutionFile);
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </ReactSortable>
                                        </Box>
                                    )}
                                    {overallMode === "followup" && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 200, mb: 0.5 }}> Upload Solution Files (Images/Videos, max 20MB each) </Typography>
                                            <Box sx={{ display: "flex", gap: 2, mb: 0.8 }}>
                                                <MUIButton variant="outlined" color="primary" onClick={() => handleFileButtonClick(true)} sx={{ textTransform: "none" }} disabled={isSubmitting} >
                                                    Select Solution Files
                                                </MUIButton>
                                                <input type="file" multiple accept="image/*,video/*" ref={solutionFileInputRef} style={{ display: "none" }}
                                                    onChange={(e) => {
                                                        console.log('[SolutionFileInput] onChange triggered');
                                                        handleFileChange(e, true);
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="body2" color="textSecondary">
                                                {solutionFiles.length > 0 ? `${solutionFiles.length} new file(s) selected` : "No new solution files selected"}
                                                {fetchedSolutionFiles.length > 0 && ` (${fetchedSolutionFiles.length} existing)`}
                                                {(fetchedSolutionFiles.length + solutionFiles.length) > 0 && ` (${fetchedSolutionFiles.length + solutionFiles.length} total)`}
                                            </Typography>
                                            {(allFetchedSolutionFiles.length > 0 || allSolutionFiles.length > 0) && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Solution Files</Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', marginTop: '16px' }}>
                                                        {allFetchedSolutionFiles.map((file, idx) => (
                                                            <Box key={file.id} onClick={() => handleOpenPreview(file)} sx={{ position: "relative", width: 100, height: 100, borderRadius: 1, overflow: "hidden", border: `1px solid ${themeColors.neutral.lineStrong}`, mx: 1, my: 0.5, display: "inline-block", cursor: "pointer" }} >
                                                                {file.url && file.mimeType.toLowerCase().startsWith("image/") ? (
                                                                    <img src={file.url} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                            console.error(`[Image] Failed to load solution image: ${file.name}, MIME: ${file.mimeType}`);
                                                                        }}
                                                                    />
                                                                ) : file.url && file.mimeType.toLowerCase().startsWith("video/") ? (
                                                                    <video style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                            console.error(`[Video] Failed to load solution video: ${file.name}, MIME: ${file.mimeType}`);
                                                                        }}
                                                                    >
                                                                        <source src={file.url} type={file.mimeType} /> Your browser does not support the video tag.
                                                                    </video>
                                                                ) : (
                                                                    <Box sx={{ p: 0, bgcolor: "error.light", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                        <Typography variant="caption" color="error">
                                                                            {file.url ? `Unsupported: ${file.mimeType}` : `No data for ${file.name}`}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ))}
                                                        {allSolutionFiles.map((file, idx) => (
                                                            <Box key={file.id} onClick={() => handleOpenPreview(file)} sx={{ position: "relative", width: 100, height: 100, borderRadius: 1, overflow: "hidden", border: `1px solid ${themeColors.neutral.lineStrong}`, mx: 1, my: 0.5, display: "inline-block", "&:hover .remove-btn": { opacity: 1 }, cursor: "pointer" }} >
                                                                {file.url && file.mimeType.toLowerCase().startsWith("image/") ? (
                                                                    <img src={file.url} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                            console.error(`[Image] Failed to load solution image: ${file.name}, MIME: ${file.mimeType}`);
                                                                        }}
                                                                    />
                                                                ) : file.url && file.mimeType.toLowerCase().startsWith("video/") ? (
                                                                    <video style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = "none";
                                                                            console.error(`[Video] Failed to load solution video: ${file.name}, MIME: ${file.mimeType}`);
                                                                        }}
                                                                    >
                                                                        <source src={file.url} type={file.mimeType} /> Your browser does not support the video tag.
                                                                    </video>
                                                                ) : (
                                                                    <Box sx={{ p: 0, bgcolor: "error.light", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                                        <Typography variant="caption" color="error">
                                                                            {file.url ? `Unsupported: ${file.mimeType}` : `No data for ${file.name}`}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                <Box className="remove-btn" sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0, 0, 0, 0.3)", opacity: 0, transition: "opacity 0.2s ease-in-out" }} >
                                                                    <CloseIcon fontSize="medium" sx={{ cursor: "pointer", color: "white", backgroundColor: "rgba(0,0,0,0.6)", borderRadius: "50%", padding: 0.5 }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleRemoveFile(file.id, file.isNew, file.isSolutionFile);
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Preview Pop-up */}
                    <Dialog open={!!previewFile} onClose={handleClosePreview} maxWidth="md" fullWidth sx={{ '& .MuiDialog-paper': { bgcolor: 'transparent', boxShadow: 'none' } }} >
                        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(0,0,0,0.8)', p: 0 }} >
                            {previewFile && previewFile.url && previewFile.mimeType.toLowerCase().startsWith('image/') ? (
                                <Zoom>
                                    <img src={previewFile.url} alt={previewFile.name} style={{ maxWidth: '90%', maxHeight: '80vh', objectFit: 'contain' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            console.error(`[Preview] Failed to load preview image: ${previewFile.name}, MIME: ${previewFile.mimeType}`);
                                        }}
                                    />
                                </Zoom>
                            ) : previewFile && previewFile.url && previewFile.mimeType.toLowerCase().startsWith('video/') ? (
                                <video controls autoPlay style={{ maxWidth: '90%', maxHeight: '85vh', objectFit: 'contain' }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        console.error(`[Preview] Failed to load preview video: ${previewFile.name}, MIME: ${previewFile.mimeType}`);
                                    }}
                                >
                                    <source src={previewFile.url} type={previewFile.mimeType} /> Your browser does not support the video tag.
                                </video>
                            ) : (
                                <Typography variant="body1" color="white"> Unable to preview: {previewFile?.name || 'Unknown'} </Typography>
                            )}
                            <CloseIcon onClick={handleClosePreview} sx={{ position: 'absolute', top: 10, right: 10, color: 'white', cursor: 'pointer', bgcolor: 'rgba(0,0,0,0.6)', borderRadius: '50%', padding: 0.5 }} />
                        </DialogContent>
                    </Dialog>

                    {formErrors.length > 0 && (
                        <Box sx={{ mx: "auto", maxWidth: 400, p: 2, bgcolor: "red.50", border: 1, borderColor: "red.500", borderRadius: 1, color: "red.700", mb: 4 }} >
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M12 5a7 7 0 110 14 7 7 0 010-14z"></path>
                                </svg>
                                <span style={{ fontWeight: "bold" }}>Please fix the following errors:</span>
                            </Box>
                            <ul style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}>
                                {formErrors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                ))}
                            </ul>
                        </Box>
                    )}
                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mt: 3, justifyContent: "center" }}>
                        <Button type="submit" disabled={isSubmitting || (overallMode === "followup" && leadStatusValue === "Failed")} > {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"} </Button>
                        {overallMode !== "followup" && ( <Button onClick={handleClose} variant="contained" color="error" disabled={isSubmitting} > Close </Button> )}
                    </Box>
                </Box>
            </Box>
        </FormContainer>
    );
}

export default ComplainForm;